/**
 * Interfaz para representar un evento del calendario
 */
export interface CalendarEvent {
  id: string;
  title: string;
  description?: string | undefined;
  location?: string | undefined;
  startTime: Date;
  endTime: Date;
  status: EventStatus;
  color?: string | undefined;
  attendees?: Attendee[] | undefined;
  isAllDay: boolean;
  // Información del calendario al que pertenece el evento
  calendarName?: string | undefined;
  calendarId?: string | undefined;
  calendarColor?: string | undefined;
}

/**
 * Estados posibles de un evento
 */
export enum EventStatus {
  CONFIRMED = 'confirmed',
  TENTATIVE = 'tentative',
  CANCELLED = 'cancelled'
}

/**
 * Interfaz para representar un asistente a un evento
 */
export interface Attendee {
  email: string;
  displayName?: string | undefined;
  responseStatus: AttendeeResponseStatus;
}

/**
 * Estados de respuesta de un asistente
 */
export enum AttendeeResponseStatus {
  ACCEPTED = 'accepted',
  DECLINED = 'declined',
  TENTATIVE = 'tentative',
  NEEDS_ACTION = 'needsAction'
}

/**
 * Interfaz para representar información de una semana
 */
export interface WeekInfo {
  start: Date;
  end: Date;
  isCurrentWeek: boolean;
}

/**
 * Interfaz para la configuración de autenticación de Google
 */
export interface GoogleAuthConfig {
  clientId: string;
  scopes: string[];
  discoveryDocs: string[];
}

/**
 * Interfaz para la configuración del propietario del calendario
 */
export interface OwnerConfig {
  email: string;
  whitelist: string[];
}

/**
 * Interfaz para el estado de autenticación
 */
export interface AuthState {
  isSignedIn: boolean;
  user?: GoogleUser;
  accessToken?: string;
}

/**
 * Interfaz para representar un usuario de Google
 */
export interface GoogleUser {
  id: string;
  email: string;
  name: string;
  imageUrl?: string;
  accessToken?: string;
}

/**
 * Interfaz para las opciones de consulta del calendario
 */
export interface CalendarQueryOptions {
  timeMin: Date;
  timeMax: Date;
  singleEvents: boolean;
  orderBy: string;
  maxResults?: number;
}

/**
 * Interfaz para representar un día de la semana en el grid del calendario
 */
export interface CalendarDay {
  date: Date;
  dayName: string;
  dayNumber: number;
  isToday: boolean;
  events: CalendarEvent[];
}

/**
 * Enumeración para los días de la semana
 */
export enum DayOfWeek {
  MONDAY = 0,
  TUESDAY = 1,
  WEDNESDAY = 2,
  THURSDAY = 3,
  FRIDAY = 4,
  SATURDAY = 5,
  SUNDAY = 6
}

/**
 * Interfaz para las opciones de configuración de la UI
 */
export interface UIConfig {
  timeFormat: '12h' | '24h';
  startHour: number;
  endHour: number;
  language: string;
}

/**
 * Tipo para manejar errores de la aplicación
 */
export interface AppError {
  code: string;
  message: string;
  details?: unknown;
}

/**
 * Interfaz para las respuestas de la API de Google Calendar
 */
export interface GoogleCalendarResponse {
  kind: string;
  etag: string;
  summary: string;
  items: GoogleCalendarEventItem[];
  nextPageToken?: string;
}

/**
 * Interfaz para un elemento de evento de Google Calendar
 */
export interface GoogleCalendarEventItem {
  kind: string;
  etag: string;
  id: string;
  status: string;
  htmlLink: string;
  created: string;
  updated: string;
  summary: string;
  description?: string;
  location?: string;
  colorId?: string;
  creator: {
    email: string;
    displayName?: string;
  };
  organizer: {
    email: string;
    displayName?: string;
  };
  start: {
    date?: string;
    dateTime?: string;
    timeZone?: string;
  };
  end: {
    date?: string;
    dateTime?: string;
    timeZone?: string;
  };
  attendees?: Array<{
    email: string;
    displayName?: string;
    responseStatus: string;
  }>;
  reminders?: {
    useDefault: boolean;
    overrides?: Array<{
      method: string;
      minutes: number;
    }>;
  };
}
